import React from 'react';
import { type Column, flexRender, type HeaderGroup } from '@tanstack/react-table';
import {
    ChevronUp, ChevronDown, X
} from 'lucide-react';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from '@/global/base/dropdown-menu.tsx';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/global/base/select.tsx';
import { Separator } from '@/global/base/separator.tsx';
import { Input } from '@/global/base/input.tsx';
import { Button } from '@/global/base/button.tsx';
import type { ColumnMeta } from './DataTable';
import type { Option } from '@/global/form/FilteredSelectField.tsx';
import DateColumnFilter from './DateColumnFilter';
import DateRangeColumnFilter from './DateRangeColumnFilter';

// TODO: This component can definitely use some reorganisation
// The JSX contains way too much logic making the code harder to read

// Determine alignment class based on meta.align or default to 'text-left'

const CustomTableHeader = <TData,>({
    headerGroup,
    tableSlug = 'data-table'
}: {
    headerGroup: HeaderGroup<TData>;
    tableSlug?: string;
}) => {
    const renderFilter = (columnMeta: ColumnMeta | undefined, column: Column<TData, unknown>) => {
        if (columnMeta?.type === 'date') {
            return <DateColumnFilter column={column} />;
        } else if (columnMeta?.type === 'dateRange') {
            return <DateRangeColumnFilter column={column} />;
        } else if (columnMeta?.type === 'select' && columnMeta?.options !== undefined) {
            const normalizedOptions: Option[] = columnMeta.options.map((option) => (typeof option === 'string' ? { value: option, label: option } : option)
            );

            return (
                <Select
                    value={(column.getFilterValue() as string) ?? ''}
                    onValueChange={(event) => column.setFilterValue(event)}>
                    <SelectTrigger
                        id={'treatment-trigger'}
                        className="data-[size=default]:h-12 h-12 w-full bg-white"
                        data-testid={`${tableSlug}-column-filter-select-${column.id}`}
                    >
                        <SelectValue placeholder="Select a filter" />
                    </SelectTrigger>
                    <SelectContent id={'treatment-content'} >
                        {normalizedOptions.map((option) => (
                            <SelectItem
                                key={option.value}
                                value={option.value}
                                className="hover:!bg-primary-accent data-[state=checked]:!bg-primary data-[state=checked]:**:text-primary-accent"
                                data-testid={`${tableSlug}-column-filter-option-${column.id}-${option.value}`}
                            >
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        } else {
            return (
                <Input
                    className="mt-2 h-8 bg-white"
                    placeholder="Filter..."
                    data-testid={`${tableSlug}-column-filter-input-${column.id}`}
                    value={(column.getFilterValue() as string) ?? ''}
                    onChange={(event) => column.setFilterValue(event.target.value)}
                    maxLength={30}
                />
            );
        }
    };

    return (
        <tr data-testid={`${tableSlug}-header-row`}>
            {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as ColumnMeta | undefined;

                const alignClass = (() => {
                    if (meta?.align === 'center') return 'text-center';
                    if (meta?.align === 'right') return 'text-right';

                    return 'text-left';
                })();

                return (
                    <th
                        data-testid={`${tableSlug}-column-${header.id}`}
                        key={header.id}
                        className={`whitespace-normal ${alignClass} px-2`}
                    >
                        {meta?.customHeaderComponent
                            ? flexRender(header.column.columnDef.header, header.getContext())
                            : <DropdownMenu>
                                <DropdownMenuTrigger
                                    className={meta?.disableFilterMenu ? 'cursor-default' : ''}
                                    data-testid={`${tableSlug}-column-menu-trigger-${header.id}`}
                                >
                                    <div className="flex items-center justify-between gap-1">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {{
                                            asc: <ChevronUp className="h-4 w-4" />,
                                            desc: <ChevronDown className="h-4 w-4" />
                                        }[header.column.getIsSorted() as 'asc' | 'desc' || 'none']}
                                    </div>
                                </DropdownMenuTrigger>
                                {!meta?.disableFilterMenu && <DropdownMenuContent
                                    className="p-2 w-[180px] bg-turqoise-secondary"
                                    data-testid={`${tableSlug}-column-menu-${header.id}`}
                                >
                                    <DropdownMenuArrow />
                                    {/* Sorting Dropdown */}
                                    <Select
                                        value={header.column.getIsSorted() || 'none'}
                                        onValueChange={(value) => {
                                            if (value === 'asc') {
                                                header.column.toggleSorting(false);
                                            } else if (value === 'desc') {
                                                header.column.toggleSorting(true);
                                            } else {
                                                header.column.clearSorting();
                                            }
                                        }}
                                    >
                                        <SelectTrigger
                                            className="my-2 w-full bg-white"
                                            data-testid={`${tableSlug}-column-sort-select-${header.id}`}
                                        >
                                            <SelectValue placeholder="Sorting" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Sorting</SelectItem>
                                            <SelectItem value="asc">Sort Ascending</SelectItem>
                                            <SelectItem value="desc">Sort Descending</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Separator className="bg-white" />

                                    {/* Render the filter input */}
                                    {/* TODO: The indentation errors below are an artifact of ternaries */}
                                    {header.column.getCanFilter() &&
                                        <div className="relative mt-2">
                                            {renderFilter(meta, header.column)}
                                            {!['date', 'dateRange', 'select'].includes(meta?.type ?? '') &&
                                                <button
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-primary"
                                                    data-testid={`${tableSlug}-column-filter-clear-${header.id}`}
                                                    onClick={() => header.column.setFilterValue('')}
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            }
                                        </div>
                                    }

                                    {/* Clear Button */}
                                    <div className="flex justify-end mt-2">
                                        <Button
                                            variant="link"
                                            className="text-sm text-primary underline"
                                            data-testid={`${tableSlug}-column-clear-${header.id}`}
                                            onClick={() => {
                                                header.column.clearSorting();
                                                header.column.setFilterValue('');
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </DropdownMenuContent>}
                            </DropdownMenu>
                        }
                    </th>
                );
            })}
        </tr>
    );
};

export default CustomTableHeader;
